import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import {
  createTemplate,
  getGetTemplateListQueryKey,
  getTemplate,
} from "~/api/custom-reports/custom-reports";
import { useToast } from "~/hooks/use-toast";

interface DuplicateTemplateArgs {
  organizationId: string;
  templateId: string;
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const mutation = useMutation({
    mutationFn: async ({
      organizationId,
      templateId,
    }: DuplicateTemplateArgs) => {
      const template = await getTemplate(organizationId, templateId);

      const newName = `${template.name} Copy`;
      const sections = (template.sections ?? []).map(
        ({ title, purpose, prompt, contextType }) => ({
          title,
          purpose:
            contextType === "docList"
              ? "List all document titles by category"
              : purpose,
          prompt: contextType === "docList" ? "docList" : prompt,
          contextType: contextType ?? "snapshot",
        })
      );

      return createTemplate(organizationId, {
        name: newName,
        definition: template.definition ?? "",
        sections,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getGetTemplateListQueryKey(variables.organizationId),
      });
    },
    onError: () => {
      toast({
        title: intl.formatMessage({
          id: "template.duplicate.error",
          defaultMessage: "Failed to duplicate template",
        }),
      });
    },
  });

  return mutation;
}
